import React, { useState, useEffect } from "react";
import axios from "axios";
import "../css_pages/table.css";
import "../css_pages/form.css";

const API_URL = "https://api.allianceprotravaux.be/api/users";

const EMPTY_FORM = {
  name: "",
  email: "",
  password: "",
  role: "user",
  is_active: true,
  is_admin: false,
};

/* Normalize a user from the API response into a consistent shape.
   - Boolean(Number(...)) safely handles 0, 1, "0", "1", true, false
     which is necessary because MySQL returns tinyint as 0/1 strings */
const normalizeUser = (user) => ({
  ...user,
  isActive: Boolean(Number(user.is_active)),
  isAdmin:  Boolean(Number(user.is_admin)),
});

export default function Users({ search }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDrawer, setShowDrawer] = useState(false);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [editingId, setEditingId] = useState(null);
  const [successMessage, setSuccessMessage] = useState("");

  /* Fetch all users on mount */
  useEffect(() => {
    const load = async () => {
      try {
        const res = await axios.get(API_URL);
        setUsers(res.data.map((u) => normalizeUser(u)));
      } catch (err) {
        console.error("Failed to load users:", err);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  /* Show a success message for 3 seconds then clear it */
  const showSuccess = (msg) => {
    setSuccessMessage(msg);
    setTimeout(() => setSuccessMessage(""), 3000);
  };

  /* Open the drawer for adding (no argument) or editing (pass the user) */
  const openDrawer = (user = null) => {
    if (user) {
      setFormData({
        name: user.name,
        email: user.email,
        password: "", // never pre-fill password
        role: user.role || "user",
        is_active: user.isActive,
        is_admin: user.isAdmin,
      });
      setEditingId(user.id);
    } else {
      setFormData(EMPTY_FORM);
      setEditingId(null);
    }
    setShowDrawer(true);
  };

  /* Close the drawer and reset form state */
  const closeDrawer = () => {
    setShowDrawer(false);
    setFormData(EMPTY_FORM);
    setEditingId(null);
  };

  /* Submit the form — creates a new user or updates an existing one */
  const handleSubmit = async (e) => {
    e.preventDefault();

    const payload = {
      name: formData.name,
      email: formData.email,
      role: formData.role,
      is_active: formData.is_active ? 1 : 0,
      is_admin: formData.is_admin ? 1 : 0,
    };

    // Only include password if it was filled in
    if (formData.password) {
      payload.password = formData.password;
    }

    try {
      if (editingId) {
        const res = await axios.put(`${API_URL}/${editingId}`, payload);
        setUsers(users.map((u) =>
          u.id === editingId ? normalizeUser(res.data) : u
        ));
        showSuccess("Utilisateur modifié avec succès !");
      } else {
        const res = await axios.post(API_URL, payload);
        setUsers([...users, normalizeUser(res.data)]);
        showSuccess("Utilisateur ajouté avec succès !");
      }
      closeDrawer();
    } catch (err) {
      console.error("Failed to save user:", err);
      alert("Erreur lors de l'enregistrement de l'utilisateur.");
    }
  };

  /* Toggle a user's active/inactive status */
  const toggleStatus = async (id) => {
    const user = users.find((u) => u.id === id);
    if (!user) return;
    try {
      const res = await axios.put(`${API_URL}/${id}`, {
        ...user,
        is_active: !user.isActive ? 1 : 0,
      });
      setUsers(users.map((u) =>
        u.id === id ? normalizeUser(res.data) : u
      ));
    } catch (err) {
      console.error("Failed to toggle status:", err);
    }
  };

  /* Delete a user after confirmation */
  const deleteUser = async (id) => {
    if (!window.confirm("Êtes-vous sûr de vouloir supprimer cet utilisateur ?")) return;
    try {
      await axios.delete(`${API_URL}/${id}`);
      setUsers(users.filter((u) => u.id !== id));
    } catch (err) {
      console.error("Failed to delete user:", err);
      alert("Erreur lors de la suppression.");
    }
  };

  /* Filter users by name or email based on the search prop */
  const filtered = users.filter((user) => {
    const q = (search || "").toLowerCase();
    return (
      user.name.toLowerCase().includes(q) ||
      (user.email || "").toLowerCase().includes(q)
    );
  });

  /* Format a date string into a readable local format */
  const formatDate = (dateStr) => {
    if (!dateStr) return "-";
    return new Date(dateStr).toLocaleDateString("fr-FR", {
      day: "2-digit",
      month: "2-digit",
      year: "numeric",
    });
  };

  return (
    <div className="categories-page" style={{ position: "relative", minHeight: "80vh" }}>

      {/* Success notification */}
      {successMessage && (
        <div style={{ padding: "10px 20px", background: "#4caf50", color: "white", marginBottom: "1rem", borderRadius: "6px" }}>
          {successMessage}
        </div>
      )}

      {/* Page header */}
      <div className="page-header" style={{ marginBottom: "2rem", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <h2>Gestion des Utilisateurs</h2>
        <button className="btn-primary" onClick={() => openDrawer()} style={{ padding: "10px 20px" }}>
          + Ajouter un utilisateur
        </button>
      </div>

      {/* Users table */}
      {loading ? (
        <p>Chargement des utilisateurs...</p>
      ) : (
        <div className="table-wrapper">
          <div className="table-header">Liste des Utilisateurs</div>
          <table className="data-table">
            <thead>
              <tr>
                <th>Nom</th>
                <th>Email</th>
                <th>Rôle</th>
                <th>Admin</th>
                <th>Statut</th>
                <th>Créé le</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length > 0 ? filtered.map((user) => (
                <tr key={user.id}>
                  <td style={{ fontWeight: "bold", width: "180px" }}>{user.name}</td>
                  <td>{user.email}</td>
                  <td>
                    <span style={{
                      padding: "3px 10px",
                      borderRadius: "12px",
                      fontSize: "0.8rem",
                      background: user.role === "admin" ? "#e8f0fe" : "#f1f3f4",
                      color: user.role === "admin" ? "#1a73e8" : "#444",
                      fontWeight: "600",
                      textTransform: "capitalize",
                    }}>
                      {user.role || "user"}
                    </span>
                  </td>
                  <td>
                    <span style={{
                      padding: "3px 10px",
                      borderRadius: "12px",
                      fontSize: "0.8rem",
                      background: user.isAdmin ? "#fce8e6" : "#f1f3f4",
                      color: user.isAdmin ? "#d93025" : "#444",
                      fontWeight: "600",
                    }}>
                      {user.isAdmin ? "Oui" : "Non"}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`badge ${user.isActive ? "active" : "inactive"}`}
                      onClick={() => toggleStatus(user.id)}
                      style={{ cursor: "pointer" }}
                    >
                      {user.isActive ? "Actif" : "Inactif"}
                    </span>
                  </td>
                  <td style={{ color: "#888", fontSize: "0.85rem" }}>{formatDate(user.created_at)}</td>
                  <td>
                    <div className="table-actions">
                      <button className="edit-btn" onClick={() => openDrawer(user)}>Modifier</button>
                      <button className="delete-btn" onClick={() => deleteUser(user.id)}>Supprimer</button>
                    </div>
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan="7" style={{ textAlign: "center" }}>Aucun utilisateur trouvé</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Drawer — slide-in panel for add/edit form */}
      {showDrawer && (
        <>
          {/* Backdrop */}
          <div
            style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.3)", zIndex: 1000, backdropFilter: "blur(2px)" }}
            onClick={closeDrawer}
          />

          {/* Panel */}
          <div style={{
            position: "fixed", top: 0, right: 0,
            width: "100%", maxWidth: "400px", height: "100vh",
            background: "white", zIndex: 1001,
            boxShadow: "-4px 0 15px rgba(0,0,0,0.1)",
            padding: "30px", display: "flex", flexDirection: "column",
            overflowY: "auto",
          }}>
            {/* Drawer header */}
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "30px" }}>
              <h3 style={{ margin: 0 }}>{editingId ? "Modifier Utilisateur" : "Ajouter Utilisateur"}</h3>
              <button onClick={closeDrawer} style={{ background: "none", border: "none", fontSize: "24px", cursor: "pointer", color: "#666" }}>
                ×
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "20px" }}>

              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Nom complet</label>
                <input
                  type="text"
                  placeholder="Ex: Jean Dupont"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  required
                  autoFocus
                />
              </div>

              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Adresse email</label>
                <input
                  type="email"
                  placeholder="Ex: jean@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  required
                />
              </div>

              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>{editingId ? "Nouveau mot de passe (laisser vide pour ne pas changer)" : "Mot de passe"}</label>
                <input
                  type="password"
                  placeholder={editingId ? "Laisser vide pour conserver" : "Mot de passe..."}
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  required={!editingId}
                  minLength={editingId ? 0 : 6}
                />
              </div>

              <div className="form-group" style={{ marginBottom: 0 }}>
                <label>Rôle</label>
                <select
                  value={formData.role}
                  onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                  style={{ width: "100%", padding: "10px 12px", borderRadius: "8px", border: "1px solid #ddd", fontSize: "14px" }}
                >
                  <option value="user">Utilisateur</option>
                  <option value="admin">Administrateur</option>
                  <option value="staff">Staff</option>
                </select>
              </div>

              <div className="form-group" style={{ marginBottom: 0, display: "flex", alignItems: "center", gap: "10px" }}>
                <input
                  type="checkbox"
                  id="is_active"
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  style={{ width: "20px", height: "20px", cursor: "pointer" }}
                />
                <label htmlFor="is_active" style={{ marginBottom: 0, cursor: "pointer" }}>Compte actif</label>
              </div>

              <div className="form-group" style={{ marginBottom: 0, display: "flex", alignItems: "center", gap: "10px" }}>
                <input
                  type="checkbox"
                  id="is_admin"
                  checked={formData.is_admin}
                  onChange={(e) => setFormData({ ...formData, is_admin: e.target.checked })}
                  style={{ width: "20px", height: "20px", cursor: "pointer" }}
                />
                <label htmlFor="is_admin" style={{ marginBottom: 0, cursor: "pointer" }}>Accès administrateur</label>
              </div>

              <div style={{ marginTop: "auto", display: "flex", gap: "10px", paddingTop: "10px" }}>
                <button type="submit" className="btn-primary" style={{ flex: 1 }}>
                  {editingId ? "Enregistrer" : "Ajouter"}
                </button>
                <button type="button" className="btn-secondary" onClick={closeDrawer} style={{ flex: 1 }}>
                  Annuler
                </button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
}